import { useState, useCallback } from "react";
import axios from "axios";

// Hook to handle AI-generated SQL queries
export const useAiPrompt = (setSqlQuery) => {
    const [prompt, setPrompt] = useState("");
    const [promptLoading, setPromptLoading] = useState(false);
    const [promptError, setPromptError] = useState(null);
    
    // Process the prompt and generate SQL query
    const processPrompt = useCallback(async () => {
        if (!prompt.trim()) return;
        
        setPromptLoading(true);
        setPromptError(null);
        
        try {
            // Call the AI generate endpoint with the prompt
            const response = await axios.post("/ai/generate", {
                prompt: prompt,
                systemMessage: "You are an AI assistant tasked with creating SQLite queries based on user requests. Only respond with the query itself, without any explanations or additional content."
            }, {
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${localStorage.getItem('authToken')}`
                }
            });
            
            // Check if the request was successful
            if (response.data.isSuccess && response.data.data.text) {
                // Extract SQL content from response, removing backticks and language indicators
                let sqlContent = response.data.data.text.trim();
                
                // Remove markdown SQL code block formatting if present
                if (sqlContent.startsWith("```") && sqlContent.endsWith("```")) {
                    // Remove opening and closing backticks
                    sqlContent = sqlContent.substring(3, sqlContent.length - 3).trim();
                    
                    // Remove SQL language indicator if present
                    if (sqlContent.toLowerCase().startsWith("sql")) {
                        sqlContent = sqlContent.substring(3).trim();
                    }
                }
                
                // Set the generated SQL query in the parent component
                setSqlQuery(sqlContent);
            } else {
                // Handle API error
                setPromptError({
                    isSuccess: false,
                    message: "Failed to generate SQL query",
                    errors: { query: ["AI service could not generate a valid response"] }
                });
            }
        } catch (error) {
            setPromptError({
                isSuccess: false,
                message: "Failed to connect to AI service",
                errors: { query: [error.response?.data?.message || "An unknown error occurred"] }
            });
        } finally {
            setPromptLoading(false);
        }
    }, [prompt, setSqlQuery]);
    
    return {
        prompt,
        setPrompt,
        promptLoading,
        promptError,
        processPrompt
    };
};